<template>
  <div class="filters-wrapper">
    <!-- Data Range com Datepicker -->
    <div class="filter">
      <DatePicker
        ref="datepicker"
        v-model="tempDateRange"
        range
        format="DD/MM/YYYY"
        :enable-time-picker="false"
        :partial-range="true"
        :auto-apply="false"
        :min-date="minSelectableDate"
        :max-date="maxSelectableDate"
        :disabled-date="isDateDisabled"
        :multi-calendars="false"
      >
        <template #input>
          <button class="filter-button" @click="openCalendar">
            {{ formattedDateRange }}
          </button>
        </template>
      </DatePicker>
    </div>

    <!-- Motivo do fechamento -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('reason')">
          {{
            selectedReasons.length
              ? $t('CLOSING_REASONS.COMMON.SELECTED_COUNT', {
                  count: selectedReasons.length,
                })
              : $t('CLOSING_REASONS.FILTERS.ALL_REASONS')
          }}
        </button>
        <div v-if="openDropdown === 'reason'" class="dropdown-content">
          <input
            type="text"
            v-model="reasonSearch"
            class="search-input"
            :placeholder="$t('CLOSING_REASONS.FILTERS.TYPE_TO_SEARCH')"
          />
          <div
            v-for="reason in filteredReasons"
            :key="reason.id"
            class="checkbox-option"
          >
            <label>
              <input
                type="checkbox"
                :value="reason.id"
                v-model="selectedReasons"
                @change="emitFilters"
              />
              {{ reason.name.name }}
            </label>
            <span class="count">{{ reason.countFormatted }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Resolvido por -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('resolver')">
          {{
            selectedResolvers.length
              ? `${selectedResolvers.length} selecionado(s)`
              : $t('CLOSING_REASONS.FILTERS.ALL_RESOLVERS')
          }}
        </button>
        <div v-if="openDropdown === 'resolver'" class="dropdown-content">
          <input
            type="text"
            v-model="resolverSearch"
            class="search-input"
            placeholder="Digite para pesquisar"
          />
          <div
            v-for="resolver in filteredResolvers"
            :key="resolver.id"
            class="checkbox-option"
          >
            <label>
              <input
                type="checkbox"
                :value="resolver.id"
                v-model="selectedResolvers"
                @change="emitFilters"
              />
              {{ resolver.name }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Caixa de entrada -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('inbox')">
          {{
            selectedInboxes.length
              ? `${selectedInboxes.length} selecionada(s)`
              : $t('CLOSING_REASONS.FILTERS.INBOX')
          }}
        </button>
        <div v-if="openDropdown === 'inbox'" class="dropdown-content">
          <input
            type="text"
            v-model="inboxSearch"
            class="search-input"
            placeholder="Digite para pesquisar"
          />
          <div
            v-for="inbox in filteredInboxes"
            :key="inbox.name"
            class="checkbox-option"
          >
            <label>
              <input
                type="checkbox"
                :value="inbox.name"
                v-model="selectedInboxes"
                @change="emitFilters"
              />
              {{ inbox.name }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'ClosingReasonsFilters',
  components: {
    DatePicker,
  },
  props: {
    reasonsData: {
      type: Array,
      required: true,
    },
  },
  data() {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      tempDateRange: [thirtyDaysAgo, today],
      customDateRange: [thirtyDaysAgo, today],
      selectedReason: '',
      selectedResolver: '',
      selectedInbox: '',
      selectedReasons: [],
      selectedResolvers: [],
      selectedInboxes: [],
      reasonSearch: '',
      resolverSearch: '',
      inboxSearch: '',
      openDropdown: null,
    };
  },
  computed: {
    reasons() {
      const unique = {};
      return this.reasonsData
        .map(item => item.closing_reason)
        .filter(reason => {
          if (!reason) return false;
          if (unique[reason.id]) return false;
          unique[reason.id] = true;
          return true;
        });
    },
    resolvers() {
      const unique = {};
      return this.reasonsData
        .map(item => item.resolved_by)
        .filter(resolver => {
          if (!resolver) return false;
          if (unique[resolver.id]) return false;
          unique[resolver.id] = true;
          return true;
        });
    },
    formattedDateRange() {
      const [start, end] = this.tempDateRange;
      if (!start || !end)
        return this.$t('CLOSING_REASONS.FILTERS.SELECT_DATE_RANGE');
      const format = d =>
        new Date(d).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      return `${format(start)} ~ ${format(end)}`;
    },
    inboxes() {
      const unique = {};
      return this.reasonsData
        .map(item => ({ name: item.inbox_name }))
        .filter(inbox => {
          if (!inbox.name || unique[inbox.name]) return false;
          unique[inbox.name] = true;
          return true;
        });
    },
    filteredReasons() {
      const counts = {};
      this.reasonsData.forEach(item => {
        const id = item.closing_reason?.id;
        if (id) counts[id] = (counts[id] || 0) + 1;
      });

      return this.reasons
        .filter(r =>
          r.name.name.toLowerCase().includes(this.reasonSearch.toLowerCase())
        )
        .map(r => ({
          ...r,
          count: counts[r.id] || 0,
          countFormatted: this.formatCount(counts[r.id] || 0),
        }));
    },
    filteredResolvers() {
      return this.resolvers.filter(r =>
        r.name.toLowerCase().includes(this.resolverSearch.toLowerCase())
      );
    },
    filteredInboxes() {
      return this.inboxes.filter(i =>
        i.name.toLowerCase().includes(this.inboxSearch.toLowerCase())
      );
    },
    today() {
      return new Date();
    },
    minSelectableDate() {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(this.today.getFullYear() - 1);
      return oneYearAgo;
    },
    maxSelectableDate() {
      return this.today;
    },
  },
  watch: {
    tempDateRange(newRange) {
      if (newRange[0] && newRange[1]) {
        this.customDateRange = newRange;

        this.selectedReasons = [];
        this.selectedResolvers = [];
        this.selectedInboxes = [];
        this.reasonSearch = '';
        this.resolverSearch = '';
        this.inboxSearch = '';
        this.openDropdown = null;

        this.emitFilters();
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    emitFilters() {
      this.$emit('filter-change', {
        customDateRange: this.customDateRange,
        reasonIds: this.selectedReasons,
        resolverIds: this.selectedResolvers,
        inboxNames: this.selectedInboxes,
      });
    },
    toggleDropdown(dropdownName) {
      this.openDropdown =
        this.openDropdown === dropdownName ? null : dropdownName;
    },
    openCalendar() {
      this.$refs.datepicker.openMenu?.();
    },
    formatCount(value) {
      if (value >= 1000) return `${(value / 1000).toFixed(1)} mil`;
      return `${value}`;
    },
    handleClickOutside(event) {
      const wrapper = this.$el;
      if (!wrapper || !wrapper.contains(event.target)) {
        this.openDropdown = null;
      }
    },
    isDateDisabled(date) {
      const min = this.minSelectableDate;
      const max = this.maxSelectableDate;
      return date < min || date > max;
    },
  },
};
</script>

<style>
.filters-wrapper {
  width: 280px;
  min-height: 150vh;
  background-color: #000;
  padding: 20px;
  box-sizing: border-box;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
}

.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-button {
  width: 240px;
  height: 50px;
  background: black !important;
  color: white !important;
  border: 1px solid white !important;
  padding: 10px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  appearance: none;
}

.filter-button:hover {
  background: #1a1a1a;
}

.hidden-datepicker-input {
  display: none !important;
}

::v-deep(.dp__input_icon) {
  display: none !important;
}

.custom-dropdown {
  position: relative;
  width: 240px;
}

.dropdown-content {
  position: absolute;
  top: 56px;
  background: #000;
  border: 1px solid white;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  z-index: 999;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.search-input {
  width: 100%;
  padding: 6px;
  margin-bottom: 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.checkbox-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  gap: 8px;
}

.checkbox-option label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  color: white;
}

.checkbox-option input[type='checkbox'] {
  margin: 0;
}

.count {
  color: #bbb;
  font-size: 12px;
  white-space: nowrap;
}

.mx-icon-calendar {
  display: none !important;
}

.mx-icon-clear {
  display: none !important;
}
</style>
